import React, { useState, useRef, useEffect } from "react";
import logoScrolled from "../assets/images/WhatsApp Image 2025-07-24 at 6.10.13 PM.jpeg";
import logoDefault from "../assets/images/logo.png";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";


import {
  Menu,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Home,
  Briefcase,
  BookOpen,
  Building2,
  Bus,
  Wrench,
  Hammer,
  Landmark,
  Hotel,
  Plane,
  Banknote,
  Phone,
  Users,
  Crown,
  Target,
  Mountain,
  Blocks,
  Pickaxe,
  Newspaper,
  Globe,
} from "lucide-react";

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t_api } = useLanguage();


  // Desktop dropdown states
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isCoreDivisionsHovered, setIsCoreDivisionsHovered] = useState(false);

  // Mobile dropdown states
  const [showAboutMobile, setShowAboutMobile] = useState(false);
  const [showGroupMobile, setShowGroupMobile] = useState(false);
  const [showCoreDivisionsMobile, setShowCoreDivisionsMobile] = useState(false);
  const [showOmanMobile, setShowOmanMobile] = useState(false);
  const [showQatarMobile, setShowQatarMobile] = useState(false);
  const [showUaeMobile, setShowUaeMobile] = useState(false);

  const [headerData, setHeaderData] = useState<any>({});
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 20);

      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => {
        if (data.Header) setHeaderData(data.Header);
      })
      .catch(console.error);
  }, []);

  const apiLogoUrl = headerData.logo_image || '';
  const apiNavItems: { id: number; label: string; link: string }[] = [];

  const headerLabel = (englishText: string, arabicFallback: string, apiArabicText?: string) => {
    return t_api(englishText, apiArabicText || arabicFallback);
  };

  const handleMouseEnter = (name: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 200);
  };

  // ✅ Close menu after clicking nav item
  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setIsCoreDivisionsHovered(false);

    // close mobile accordions
    setShowGroupMobile(false);
    setShowAboutMobile(false);
    setShowCoreDivisionsMobile(false);
    setShowOmanMobile(false);
    setShowQatarMobile(false);
    setShowUaeMobile(false);
  };

  const navHomeTitle = headerLabel(headerData.nav_home || 'Home', 'الرئيسية', headerData.nav_home_ar);
  const navGroupTitle = headerLabel(headerData.nav_group || 'Our Group', 'مجموعة حمدان', headerData.nav_group_ar);
  const navOmanTitle = headerLabel(headerData.country_oman || 'Oman', 'عمان', headerData.country_oman_ar);
  const navQatarTitle = headerLabel(headerData.country_qatar || 'Qatar', 'قطر', headerData.country_qatar_ar);
  const navUaeTitle = headerLabel(headerData.country_uae || 'UAE', 'الإمارات', headerData.country_uae_ar);
  const navAboutTitle = headerLabel(headerData.nav_about || 'About Us', 'من نحن', headerData.nav_about_ar);
  const navCoreDivisionsTitle = headerLabel(headerData.nav_core_divisions || 'Core Divisions', 'الأقسام الرئيسية', headerData.nav_core_divisions_ar);
  const navCareersTitle = headerLabel(headerData.nav_careers || 'Careers', 'الوظائف', headerData.nav_careers_ar);
  const navContactTitle = headerLabel(headerData.nav_contact || 'Contact Us', 'تواصل معنا', headerData.nav_contact_ar);
  const languageSwitchLabel = language === 'en' ? 'العربية' : 'English';

  const group1Title = headerLabel(headerData.group_1 || 'Hamdan Construction', 'حمدان للإنشاءات', headerData.group_1_ar);
  const group2Title = headerLabel(headerData.group_2 || 'Hamdan Exchange', 'حمدان للصرافة', headerData.group_2_ar);
  const group3Title = headerLabel(headerData.group_3 || 'Hamdan International for Gabbro', 'حمدان الدولية للجابرو', headerData.group_3_ar);
  const group4Title = headerLabel(headerData.group_4 || 'Hamdan Mining', 'حمدان للتعدين', headerData.group_4_ar);
  const group5Title = headerLabel(headerData.group_5 || 'Hamdan Plaza Hotel', 'فندق حمدان بلازا', headerData.group_5_ar);
  const group6Title = headerLabel(headerData.group_6 || 'Hamdan Real Estates', 'حمدان للعقارات', headerData.group_6_ar);
  const group6Sub1Title = headerLabel(headerData.group_6_sub_1 || 'Al Yasminah Real Estate', 'الياسمينة للعقارات', headerData.group_6_sub_1_ar);
  const group7Title = headerLabel(headerData.group_7 || 'Hamdan Tires, Battery & Accessories', 'حمدان للإطارات والبطاريات والملحقات', headerData.group_7_ar);
  const group8Title = headerLabel(headerData.group_8 || 'Hamdan Tour And Travel', 'حمدان للسياحة والسفر', headerData.group_8_ar);
  const group9Title = headerLabel(headerData.group_9 || 'Hamdan Transport Division', 'قسم حمدان للنقل', headerData.group_9_ar);

  // ✅ Desktop main links (Fallback if API is empty)
  const fallbackNavLinks = [
    { label: navCareersTitle, icon: <BookOpen size={18} />, to: "/careers" },
  ];

  const desktopNavLinks = apiNavItems.length > 0 ? apiNavItems : fallbackNavLinks;
  const mobileNavLinks = apiNavItems.length > 0 ? apiNavItems : fallbackNavLinks;

  // ✅ About dropdown links (with icons)
  const aboutLinks = [
    { label: headerLabel(headerData.about_1 || "Hamdan Group", "مجموعة حمدان", headerData.about_1_ar), icon: <Users size={18} />, to: "/about?tab=group" },
    { label: headerLabel(headerData.about_2 || "Founder's Message", "رسالة المؤسس", headerData.about_2_ar), icon: <Crown size={18} />, to: "/about?tab=founder" },
    { label: headerLabel(headerData.about_3 || "Vision & Mission", "الرؤية والرسالة", headerData.about_3_ar), icon: <Target size={18} />, to: "/about?tab=vision" },
    { label: headerLabel(headerData.about_4 || "News", "الأخبار", headerData.about_4_ar), icon: <Newspaper size={18} />, to: "/about?tab=news" },
  ];

  // ✅ Group dropdown links
  const groupLinks = [
    { label: group1Title, icon: <Hammer size={18} />, to: "/hamdan-construction", sub: null },
    { label: group2Title, icon: <Banknote size={18} />, to: "/hamdan-exchange", sub: null },
    {
      label: group4Title,
      icon: <Landmark size={18} />,
      to: "/hamdan-mining",
      sub: [
        { label: group3Title, icon: <Blocks size={16} />, to: "/hamdan-gabbro-qatar" },
      ],
    },
    { label: group5Title, icon: <Hotel size={18} />, to: "/hamdan-plaza", sub: null },
    {
      label: group6Title,
      icon: <Building2 size={18} />,
      to: "/al-baz-real-estate",
      sub: [
        { label: group6Sub1Title, icon: <Building2 size={16} />, to: "/al-yasminah" },
      ],
    },
    { label: group7Title, icon: <Wrench size={18} />, to: "/hamdan-tyres", sub: null },
    { label: group8Title, icon: <Plane size={18} />, to: "/travel-tourism", sub: null },
    { label: group9Title, icon: <Bus size={18} />, to: "/hamdan-transport", sub: null },
  ];

  // Country Specific Lists
  const omanLinks = [
    { label: group4Title, icon: <Landmark size={18} />, to: "/hamdan-mining" },
    { label: group7Title, icon: <Wrench size={18} />, to: "/hamdan-tyres" },
    { label: group9Title, icon: <Bus size={18} />, to: "/hamdan-transport" },
    { label: group2Title, icon: <Banknote size={18} />, to: "/hamdan-exchange" },
    { label: group6Title, icon: <Building2 size={18} />, to: "/al-baz-real-estate" },
    { label: group5Title, icon: <Hotel size={18} />, to: "/hamdan-plaza" },
    { label: group8Title, icon: <Plane size={18} />, to: "/travel-tourism" },
    { label: group1Title, icon: <Hammer size={18} />, to: "/hamdan-construction" },
  ];

  const qatarLinks = [
    { label: group3Title, icon: <Blocks size={16} />, to: "/hamdan-gabbro-qatar" },
  ];

  const uaeLinks = [
    { label: group6Sub1Title, icon: <Building2 size={16} />, to: "/al-yasminah" },
  ];

  // track which dropdown item index is hovered (for sub-flyouts)
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  // Check if current path is inside any group division sub-page
  const allGroupPaths = [
    "/hamdan-construction",
    "/hamdan-exchange",
    "/hamdan-mining",
    "/hamdan-plaza",
    "/al-baz-real-estate",
    "/hamdan-tyres",
    "/travel-tourism",
    "/hamdan-transport",
    "/hamdan-gabbro-qatar",
    "/al-yasminah",
  ];
  const isGroupActive = allGroupPaths.some(p => location.pathname.startsWith(p));

  // Determine which country dropdown owns the current path
  const isOmanActive = omanLinks.some(l => location.pathname.startsWith(l.to));
  const isQatarActive = qatarLinks.some(l => location.pathname.startsWith(l.to));
  const isUaeActive = uaeLinks.some(l => location.pathname.startsWith(l.to));
  const isAnyCountryActive = isOmanActive || isQatarActive || isUaeActive;

  // About Us is active only on /about routes OR on group paths not claimed by any country dropdown
  const showAboutUnderline = location.pathname.startsWith("/about") || (isGroupActive && !isAnyCountryActive);

  const DropdownMenu = ({ title, links, isOpen, setOpen, name, layout = "compact" }: any) => {
    const isWide = layout === "wide";

    return (
      <div className="relative group/parent" onMouseEnter={() => handleMouseEnter(name)} onMouseLeave={handleMouseLeave}>
        <button className="flex items-center group relative text-[15px] font-medium text-black transition duration-300">
          <span className={`transition duration-300 relative ${isOpen ? 'text-[#F2BB13]' : 'text-black group-hover:text-[#F2BB13]'}`}>
            {title}
            <span className={`absolute left-0 -bottom-1 h-0.5 bg-[#F2BB13] transition-all duration-500 ${links.some((l: any) => location.pathname.startsWith(l.to)) ? 'w-full' : 'w-0 group-hover:w-full'}`} />
          </span>
          <ChevronDown className={`w-4 h-4 ms-1 transition-transform duration-200 ${isOpen ? "rotate-180 text-[#F2BB13]" : "text-black"}`} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Hover Bridge */}
              <div className="absolute top-full left-0 w-full h-4 z-10" />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className={`absolute ltr:left-0 rtl:right-0 mt-2 bg-white shadow-xl rounded-xl z-20 border border-gray-100 ${isWide ? 'w-[22rem] p-2' : 'w-64 py-2'}`}
              >
                {links.map((link: any, idx: number) => {
                  const active = location.pathname === link.to;
                  return (
                    <Link
                      key={idx}
                      to={link.to}
                      onClick={handleNavClick}
                      className={`flex gap-3 rounded-lg text-[14px] transition ${isWide ? 'items-start px-4 py-3 leading-snug' : 'items-center px-4 py-2'} ${active ? 'text-[#F2BB13] font-semibold bg-yellow-50' : 'text-gray-800 hover:text-[#F2BB13] hover:bg-gray-50'}`}
                    >
                      <span className={`${isWide ? 'mt-0.5' : ''} shrink-0`}>
                        {link.icon || <Building2 size={18} />}
                      </span>
                      <span className="min-w-0 break-words">{link.label}</span>
                    </Link>
                  );
                })}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const MobileAccordion = ({ title, links, isOpen, setOpen, onLinkClick }: any) => (
    <div className="border-b border-gray-100 pb-2">
      <button onClick={() => setOpen(!isOpen)} className="flex items-center justify-between w-full py-2 font-medium" type="button">
        <span className={links.some((l: any) => location.pathname.startsWith(l.to)) ? 'text-[#F2BB13]' : ''}>{title}</span>
        <ChevronDown className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="mt-2 ms-4 flex flex-col space-y-3 overflow-hidden">
            {links.map((link: any, idx: number) => (
              <Link key={idx} to={link.to} onClick={onLinkClick} className={`flex items-center gap-3 text-[15px] py-1.5 ${location.pathname === link.to ? 'text-[#F2BB13] font-bold' : 'text-gray-700'}`}>
                {link.icon || <Building2 size={16} />}
                {link.label}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const DesktopNavLink = ({ label, to }: { label: string; to: string }) => (
    <Link to={to} className="relative group text-[15px] font-medium text-black transition duration-300">
      <span className="relative text-black group-hover:text-[#F2BB13]">
        {label}
        <span className="absolute left-0 -bottom-1 h-0.5 bg-[#F2BB13] transition-all duration-500 w-0 group-hover:w-full" />
      </span>
    </Link>
  );

  return (
    <>
      <div className="fixed inset-0 bg-opacity-20 z-10 pointer-events-none" />

      <header
        data-no-auto-translate="true"
        className={`w-full z-50 fixed font-sans transition-all duration-700 ${isScrolled ? "top-4" : "top-8"
          } ${isVisible ? "translate-y-0 opacity-100" : "-translate-y-48 opacity-0 pointer-events-none"}`}
      >
        <div className="max-w-[1440px] mx-auto px-6 relative">
          <div className="relative bg-white shadow-xl" style={{ borderRadius: "0 60px 0 60px" }}>
            <div className="flex items-center justify-between px-12 py-5">
              {/* Logo */}
              <div className="flex items-center">
                <Link to="/" onClick={handleNavClick}>
                  <img
                    src={apiLogoUrl || logoScrolled}
                    alt="Hamdan Trading Group"
                    className="w-[13.5rem] h-[3rem] object-contain"
                  />
                </Link>
              </div>

              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-8">
                <nav className="flex items-center gap-8">
                  {/* Home */}
                  <Link to="/" className="relative group text-[15px] font-medium text-black transition duration-300">
                    <span className="relative text-black group-hover:text-[#F2BB13]">
                      {navHomeTitle}
                      <span className={`absolute left-0 -bottom-1 h-0.5 bg-[#F2BB13] transition-all duration-500 ${location.pathname === '/' ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                    </span>
                  </Link>

                  {/* Oman Dropdown */}
                  <DropdownMenu
                    title={navOmanTitle}
                    links={omanLinks}
                    isOpen={activeDropdown === 'oman'}
                    setOpen={(val: boolean) => val ? setActiveDropdown('oman') : setActiveDropdown(null)}
                    name="oman"
                    layout="wide"
                  />

                  {/* Qatar Dropdown */}
                  <DropdownMenu
                    title={navQatarTitle}
                    links={qatarLinks}
                    isOpen={activeDropdown === 'qatar'}
                    setOpen={(val: boolean) => val ? setActiveDropdown('qatar') : setActiveDropdown(null)}
                    name="qatar"
                  />

                  {/* UAE Dropdown */}
                  <DropdownMenu
                    title={navUaeTitle}
                    links={uaeLinks}
                    isOpen={activeDropdown === 'uae'}
                    setOpen={(val: boolean) => val ? setActiveDropdown('uae') : setActiveDropdown(null)}
                    name="uae"
                  />

                  {/* About Dropdown (Hamdan Group -> Core Divisions dropdown, Founder, Vision, News) */}
                  <div className="relative" onMouseEnter={() => handleMouseEnter('about')} onMouseLeave={handleMouseLeave}>
                    <button
                      type="button"
                      className="flex items-center group relative text-[15px] font-medium text-black hover:text-[#F2BB13] transition py-2"
                    >
                      <span className={`relative ${showAboutUnderline || activeDropdown === 'about' ? 'text-[#F2BB13]' : ''}`}>
                        {navAboutTitle}
                        <span className={`absolute left-0 -bottom-1 h-0.5 bg-[#F2BB13] transition-all duration-500 ${showAboutUnderline || activeDropdown === 'about' ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 ms-1 text-black transition-transform duration-200 ${activeDropdown === 'about' ? "rotate-180 text-[#F2BB13]" : ""}`}
                      />
                    </button>

                    <AnimatePresence>
                      {activeDropdown === 'about' && (
                        <>
                          <div className="absolute top-full left-0 w-full h-4 z-10" />
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute ltr:left-0 rtl:right-0 mt-2 w-72 bg-white shadow-xl rounded-xl py-2 z-20 border border-gray-100"
                            onMouseEnter={() => handleMouseEnter('about')}
                            onMouseLeave={handleMouseLeave}
                          >
                            {/* 1. Hamdan Group */}
                            <Link
                              to="/about?tab=group"
                              onClick={handleNavClick}
                              className={`flex items-center gap-3 px-4 py-2.5 text-[14px] transition w-full ${location.pathname === '/about' && location.search.includes('tab=group') ? 'text-[#F2BB13] font-semibold bg-yellow-50' : 'text-gray-800 hover:text-[#F2BB13] hover:bg-gray-50'}`}
                            >
                              <Users size={18} className="shrink-0 text-[#F2BB13]" />
                              <span className="flex-1 font-medium">{headerLabel(headerData.about_1 || "Hamdan Group", "مجموعة حمدان", headerData.about_1_ar)}</span>
                            </Link>

                            {/* 2. Core Divisions ▼ Dropdown Flyout */}
                            <div
                              className="relative"
                              onMouseEnter={() => setIsCoreDivisionsHovered(true)}
                              onMouseLeave={() => setIsCoreDivisionsHovered(false)}
                            >
                              <div
                                className={`flex items-center gap-3 px-4 py-2.5 text-[14px] transition w-full cursor-pointer select-none ${isGroupActive ? 'text-[#F2BB13] font-semibold bg-yellow-50' : 'text-gray-800 hover:text-[#F2BB13] hover:bg-gray-50'}`}
                              >
                                <Briefcase size={18} className="shrink-0 text-[#F2BB13]" />
                                <span className="flex-1 font-medium">{navCoreDivisionsTitle}</span>
                                <ChevronLeft className="w-4 h-4 opacity-60 ltr:block rtl:hidden text-gray-500" />
                                <ChevronRight className="w-4 h-4 opacity-60 ltr:hidden rtl:block text-gray-500" />
                              </div>

                              {/* Core Divisions Flyout Submenu (Pops out to LEFT in LTR to fit screen perfectly) */}
                              {isCoreDivisionsHovered && (
                                <div className="absolute ltr:right-full ltr:-mr-1 rtl:left-full rtl:-ml-1 top-0 w-72 bg-white shadow-2xl rounded-xl py-2 border border-gray-100 z-30">
                                  {/* Hover Bridge */}
                                  <div className="absolute top-0 ltr:-right-4 rtl:-left-4 w-4 h-full cursor-default" />
                                  {groupLinks.map((link, idx) => {
                                    const isActive = location.pathname.startsWith(link.to)
                                      || (link.sub || []).some(s => location.pathname.startsWith(s.to));
                                    const hasSub = link.sub && link.sub.length > 0;
                                    return (
                                      <div key={idx} className="flex flex-col">
                                        <Link
                                          to={link.to}
                                          onClick={handleNavClick}
                                          className={`flex items-center gap-3 px-4 py-2 text-[14px] transition w-full ${isActive ? 'text-[#F2BB13] font-semibold bg-yellow-50' : 'text-gray-800 hover:text-[#F2BB13] hover:bg-gray-50'}`}
                                        >
                                          {link.icon}
                                          <span className="flex-1">{link.label}</span>
                                          {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#F2BB13]" />}
                                        </Link>

                                        {/* Nested sub-items inside Core Divisions (e.g. Gabbro under Mining, Al Yasminah under Real Estate) */}
                                        {hasSub && (
                                          <div className="ms-8 flex flex-col border-s-2 border-yellow-300/60 ps-3 my-0.5 space-y-1">
                                            {link.sub!.map((sub, si) => {
                                              const subActive = location.pathname.startsWith(sub.to);
                                              return (
                                                <Link
                                                  key={si}
                                                  to={sub.to}
                                                  onClick={handleNavClick}
                                                  className={`flex items-center gap-2 py-1 text-[13px] transition ${subActive ? 'text-[#F2BB13] font-bold' : 'text-gray-600 hover:text-[#F2BB13]'}`}
                                                >
                                                  {sub.icon}
                                                  <span>{sub.label}</span>
                                                </Link>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* 3. Founder's Message */}
                            <Link
                              to="/about?tab=founder"
                              onClick={handleNavClick}
                              className={`flex items-center gap-3 px-4 py-2.5 text-[14px] transition w-full ${location.pathname === '/about' && location.search.includes('tab=founder') ? 'text-[#F2BB13] font-semibold bg-yellow-50' : 'text-gray-800 hover:text-[#F2BB13] hover:bg-gray-50'}`}
                            >
                              <Crown size={18} className="shrink-0 text-gray-500" />
                              <span className="flex-1 font-medium">{headerLabel(headerData.about_2 || "Founder's Message", "رسالة المؤسس", headerData.about_2_ar)}</span>
                            </Link>

                            {/* 4. Vision & Mission */}
                            <Link
                              to="/about?tab=vision"
                              onClick={handleNavClick}
                              className={`flex items-center gap-3 px-4 py-2.5 text-[14px] transition w-full ${location.pathname === '/about' && location.search.includes('tab=vision') ? 'text-[#F2BB13] font-semibold bg-yellow-50' : 'text-gray-800 hover:text-[#F2BB13] hover:bg-gray-50'}`}
                            >
                              <Target size={18} className="shrink-0 text-gray-500" />
                              <span className="flex-1 font-medium">{headerLabel(headerData.about_3 || "Vision & Mission", "الرؤية والرسالة", headerData.about_3_ar)}</span>
                            </Link>

                            {/* 5. News */}
                            <Link
                              to="/about?tab=news"
                              onClick={handleNavClick}
                              className={`flex items-center gap-3 px-4 py-2.5 text-[14px] transition w-full ${location.pathname === '/about' && location.search.includes('tab=news') ? 'text-[#F2BB13] font-semibold bg-yellow-50' : 'text-gray-800 hover:text-[#F2BB13] hover:bg-gray-50'}`}
                            >
                              <Newspaper size={18} className="shrink-0 text-gray-500" />
                              <span className="flex-1 font-medium">{headerLabel(headerData.about_4 || "News", "الأخبار", headerData.about_4_ar)}</span>
                            </Link>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>

                  {(desktopNavLinks as any[]).filter((item: any) => item.label.toLowerCase() !== navHomeTitle.toLowerCase()).map((item: any) => (
                    <DesktopNavLink key={item.label} label={item.label} to={item.link || item.to || '#'} />
                  ))}
                </nav>

                {/* Language Switcher */}
                <button
                  onClick={toggleLanguage}
                  className="me-4 px-3 py-1 text-[15px] font-medium text-gray-700 hover:text-[#F2BB13] transition-colors flex items-center gap-1"
                >
                  <Globe size={18} />
                  {languageSwitchLabel}
                </button>
                {/* Contact Button */}
                <Link
                  to="/contact"
                  className="px-6 py-2 bg-[#F2BB13] text-black text-[15px] rounded-md hover:bg-[#E8B622] transition duration-300 flex items-center gap-2"
                >
                  <Phone size={18} /> {navContactTitle}
                </Link>
              </div>
              {/* Mobile Menu Toggle */}
              <div className="md:hidden">
                <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="transition-all duration-300">
                  {isMobileMenuOpen ? (
                    <X size={28} className="text-gray-800" />
                  ) : (
                    <Menu size={28} className="text-gray-800" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - MOVED OUTSIDE HEADER TO PREVENT TRANSFORM ISSUES */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            key="mobileMenu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
            data-no-auto-translate="true"
            className="fixed inset-0 bg-white z-[100] flex flex-col p-6 overflow-y-auto font-sans"
          >
            <div className="flex justify-between items-center mb-8">
              <img src={apiLogoUrl || logoScrolled} alt="Hamdan Trading Group" className="h-10 object-contain" />
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-1 rounded-full hover:bg-gray-100 transition">
                <X size={28} className="text-gray-800" />
              </button>
            </div>

            <nav className="flex flex-col space-y-5 text-gray-800 text-[16px]">
              {/* Home FIRST */}
              <Link
                to="/"
                onClick={handleNavClick}
                className="py-2 font-medium hover:text-[#F2BB13] transition border-b border-gray-100"
              >
                {navHomeTitle}
              </Link>

              {/* Countries */}
              <MobileAccordion
                title={navOmanTitle}
                links={omanLinks}
                isOpen={showOmanMobile}
                setOpen={setShowOmanMobile}
                onLinkClick={handleNavClick}
              />
              <MobileAccordion
                title={navQatarTitle}
                links={qatarLinks}
                isOpen={showQatarMobile}
                setOpen={setShowQatarMobile}
                onLinkClick={handleNavClick}
              />
              <MobileAccordion
                title={navUaeTitle}
                links={uaeLinks}
                isOpen={showUaeMobile}
                setOpen={setShowUaeMobile}
                onLinkClick={handleNavClick}
              />

              {/* About Us Mobile Accordion */}
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => setShowAboutMobile(!showAboutMobile)}
                  className="flex items-center justify-between w-full py-2 font-medium"
                  type="button"
                >
                  <span className={showAboutUnderline ? 'text-[#F2BB13]' : ''}>{navAboutTitle}</span>
                  <ChevronDown
                    className={`transition-transform duration-200 ${showAboutMobile ? "rotate-180" : ""}`}
                  />
                </button>

                <AnimatePresence>
                  {showAboutMobile && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-2 ml-4 flex flex-col space-y-2 overflow-hidden"
                    >
                      {/* 1. Hamdan Group */}
                      <Link
                        to="/about?tab=group"
                        onClick={handleNavClick}
                        className={`flex items-center gap-3 text-[15px] py-1.5 ${location.pathname === '/about' && location.search.includes('tab=group') ? 'text-[#F2BB13] font-bold' : 'text-gray-700'}`}
                      >
                        <Users size={16} />
                        {headerLabel(headerData.about_1 || "Hamdan Group", "مجموعة حمدان", headerData.about_1_ar)}
                      </Link>

                      {/* 2. Core Divisions Accordion */}
                      <div className="border-l-2 border-yellow-400/50 pl-3 py-1 my-1">
                        <button
                          type="button"
                          onClick={() => setShowCoreDivisionsMobile(!showCoreDivisionsMobile)}
                          className="flex items-center justify-between w-full py-1 text-[15px] text-gray-800 font-semibold"
                        >
                          <span className="flex items-center gap-2">
                            <Briefcase size={16} className="text-[#F2BB13]" />
                            {navCoreDivisionsTitle}
                          </span>
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showCoreDivisionsMobile ? "rotate-180" : ""}`} />
                        </button>

                        <AnimatePresence>
                          {showCoreDivisionsMobile && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="mt-2 ml-2 flex flex-col space-y-2 overflow-hidden"
                            >
                              {groupLinks.map((link, idx) => {
                                const hasSub = link.sub && link.sub.length > 0;
                                return (
                                  <div key={idx} className="flex flex-col">
                                    {!hasSub ? (
                                      <Link
                                        to={link.to}
                                        onClick={handleNavClick}
                                        className={`flex items-center gap-2.5 text-[14px] py-1.5 ${location.pathname === link.to ? 'text-[#F2BB13] font-bold' : 'text-gray-600'}`}
                                      >
                                        {link.icon}
                                        {link.label}
                                      </Link>
                                    ) : (
                                      <div className="flex flex-col">
                                        <Link
                                          to={link.to}
                                          onClick={handleNavClick}
                                          className="flex items-center gap-2.5 text-[14px] text-gray-700 font-semibold py-1.5"
                                        >
                                          {link.icon}
                                          {link.label}
                                        </Link>
                                        <div className="ml-5 flex flex-col space-y-1.5 border-l-2 border-gray-200 pl-3 mt-1 mb-1">
                                          {link.sub.map((sub, sIdx) => (
                                            <Link
                                              key={sIdx}
                                              to={sub.to}
                                              onClick={handleNavClick}
                                              className={`flex items-center gap-2 text-[13px] py-1 ${location.pathname === sub.to ? 'text-[#F2BB13] font-bold' : 'text-gray-500'}`}
                                            >
                                              {sub.icon}
                                              {sub.label}
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* 3. Founder's Message */}
                      <Link
                        to="/about?tab=founder"
                        onClick={handleNavClick}
                        className={`flex items-center gap-3 text-[15px] py-1.5 ${location.pathname === '/about' && location.search.includes('tab=founder') ? 'text-[#F2BB13] font-bold' : 'text-gray-700'}`}
                      >
                        <Crown size={16} />
                        {headerLabel(headerData.about_2 || "Founder's Message", "رسالة المؤسس", headerData.about_2_ar)}
                      </Link>

                      {/* 4. Vision & Mission */}
                      <Link
                        to="/about?tab=vision"
                        onClick={handleNavClick}
                        className={`flex items-center gap-3 text-[15px] py-1.5 ${location.pathname === '/about' && location.search.includes('tab=vision') ? 'text-[#F2BB13] font-bold' : 'text-gray-700'}`}
                      >
                        <Target size={16} />
                        {headerLabel(headerData.about_3 || "Vision & Mission", "الرؤية والرسالة", headerData.about_3_ar)}
                      </Link>

                      {/* 5. News */}
                      <Link
                        to="/about?tab=news"
                        onClick={handleNavClick}
                        className={`flex items-center gap-3 text-[15px] py-1.5 ${location.pathname === '/about' && location.search.includes('tab=news') ? 'text-[#F2BB13] font-bold' : 'text-gray-700'}`}
                      >
                        <Newspaper size={16} />
                        {headerLabel(headerData.about_4 || "News", "الأخبار", headerData.about_4_ar)}
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Dynamic Links AFTER About */}
              {(mobileNavLinks as any[]).filter((item: any) => item.label.toLowerCase() !== navHomeTitle.toLowerCase()).map((item: any, idx) => (
                <Link
                  key={idx}
                  to={item.link || item.to || '#'}
                  onClick={handleNavClick}
                  className="py-2 font-medium hover:text-[#F2BB13] transition border-b border-gray-100"
                >
                  {item.label}
                </Link>
              ))}

            </nav>

            <button
              onClick={() => {
                toggleLanguage();
                setIsMobileMenuOpen(false);
              }}
              className="mt-6 px-6 py-3 rounded-md border border-gray-200 text-gray-800 font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition"
            >
              <Globe size={18} />
              {languageSwitchLabel}
            </button>

            <Link
              to="/contact"
              onClick={handleNavClick}
              className="mt-4 px-6 py-3 rounded-md bg-[#F2BB13] text-white font-medium text-center flex items-center justify-center gap-2 hover:bg-[#E8B622] transition"
            >
              <Phone size={18} /> {navContactTitle}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
